import React, { useCallback, useEffect, useMemo, useState } from 'react';
import '@fontsource/exo-2';
import { useSearchParams } from 'react-router-dom';
import { getFactionIconPath } from '../utils/factionHudDisplay';
import { resolvePublicAssetUrl } from '../utils/resolvePublicAssetUrl';
import './FactionRingScreenshot.css';

/** Same catalog as battle intro / order screenshot gallery. */
const FACTIONS = [
  'Remnants of Humanity',
  'Exolot Dominion',
  'League of Shadows',
  "Ra's Children",
  'Asrah',
  'The Conspiracy',
  'The Corrupted Core',
  'The Covenant',
  'The Divine Syndicate',
  'The Forsaken Pact',
  'The Hollow',
  'The Iron Alliance',
  'The Last Crusade',
  'The Shadow Consortium',
  'The Silent Accord',
  'The Sentients',
];

const factionColors = {
  'Remnants of Humanity': '#556B2F',
  'Exolot Dominion': '#4B0082',
  'League of Shadows': '#1A1A1A',
  "Ra's Children": '#F4A460',
  Asrah: '#8B0000',
  'The Sentients': '#ADD8E6',
  Combined: '#FFA500',
};

/** Matches BattleUI battle-intro orb fill (45%/55% combined gradients). */
function getFactionOrbBackground(factionName) {
  if (!factionName || factionName === 'Unknown') {
    throw new Error('FactionRingScreenshot: factionName is required');
  }

  if (factionName === 'The Conspiracy') {
    return 'linear-gradient(to right, #8B0000 45%, #1A1A1A 55%)';
  }
  if (factionName === 'The Corrupted Core') {
    return 'linear-gradient(to right, #4B0082 45%, #8B0000 55%)';
  }
  if (factionName === 'The Covenant') {
    return 'linear-gradient(to right, #F4A460 45%, #1A1A1A 55%)';
  }
  if (factionName === 'The Divine Syndicate') {
    return 'linear-gradient(to right, #F4A460 45%, #4B0082 55%)';
  }
  if (factionName === 'The Forsaken Pact') {
    return 'linear-gradient(to right, #556B2F 45%, #8B0000 55%)';
  }
  if (factionName === 'The Hollow') {
    return 'linear-gradient(to right, #F4A460 45%, #8B0000 55%)';
  }
  if (factionName === 'The Iron Alliance') {
    return 'linear-gradient(to right, #556B2F 45%, #4B0082 55%)';
  }
  if (factionName === 'The Last Crusade') {
    return 'linear-gradient(to right, #F4A460 45%, #556B2F 55%)';
  }
  if (factionName === 'The Shadow Consortium') {
    return 'linear-gradient(to right, #1A1A1A 45%, #4B0082 55%)';
  }
  if (factionName === 'The Silent Accord') {
    return 'linear-gradient(to right, #556B2F 45%, #1A1A1A 55%)';
  }

  const color = factionColors[factionName];
  if (!color) {
    throw new Error(`FactionRingScreenshot: unknown faction color for ${factionName}`);
  }
  return color;
}

function resolveFactionFromParam(raw) {
  if (raw == null || raw === '') return FACTIONS[0];
  const decoded = decodeURIComponent(String(raw));
  const exact = FACTIONS.find((name) => name === decoded);
  if (exact) return exact;
  const lower = decoded.toLowerCase();
  const caseInsensitive = FACTIONS.find((name) => name.toLowerCase() === lower);
  if (caseInsensitive) return caseInsensitive;
  throw new Error(`FactionRingScreenshot: unknown faction in URL: ${decoded}`);
}

export default function FactionRingScreenshot() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [screenshotMode, setScreenshotMode] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const selectedFaction = useMemo(() => {
    try {
      return resolveFactionFromParam(searchParams.get('faction'));
    } catch (err) {
      return null;
    }
  }, [searchParams]);

  useEffect(() => {
    try {
      resolveFactionFromParam(searchParams.get('faction'));
      setLoadError(null);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : String(err));
    }
  }, [searchParams]);

  const factionIconPath = useMemo(() => {
    if (!selectedFaction) return null;
    return getFactionIconPath(selectedFaction);
  }, [selectedFaction]);

  const selectFaction = useCallback((factionName) => {
    if (!FACTIONS.includes(factionName)) {
      throw new Error(`FactionRingScreenshot: invalid faction selection: ${factionName}`);
    }
    setSearchParams({ faction: factionName }, { replace: true });
  }, [setSearchParams]);

  const moveSelection = useCallback((direction) => {
    if (!selectedFaction) return;
    const currentIndex = FACTIONS.indexOf(selectedFaction);
    if (currentIndex < 0) {
      throw new Error(`FactionRingScreenshot: selected faction not in catalog: ${selectedFaction}`);
    }
    const nextIndex = (currentIndex + direction + FACTIONS.length) % FACTIONS.length;
    selectFaction(FACTIONS[nextIndex]);
  }, [selectedFaction, selectFaction]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.target instanceof HTMLSelectElement || event.target instanceof HTMLInputElement) {
        return;
      }
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        event.preventDefault();
        moveSelection(1);
        return;
      }
      if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        event.preventDefault();
        moveSelection(-1);
        return;
      }
      if (event.key === 's' || event.key === 'S') {
        event.preventDefault();
        setScreenshotMode((value) => !value);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [moveSelection]);

  if (loadError) {
    return (
      <div className="faction-ring-screenshot faction-ring-screenshot-error" role="alert">
        {loadError}
      </div>
    );
  }

  if (!selectedFaction || !factionIconPath) {
    return (
      <div className="faction-ring-screenshot faction-ring-screenshot-error" role="alert">
        FactionRingScreenshot: no faction selected
      </div>
    );
  }

  const rootClassName = [
    'faction-ring-screenshot',
    screenshotMode ? 'faction-ring-screenshot--screenshot-mode' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className={rootClassName}>
      <header className="faction-ring-screenshot-chrome faction-ring-screenshot-header">
        <div>
          <h1 className="faction-ring-screenshot-title">Faction ring screenshots</h1>
          <p className="faction-ring-screenshot-subtitle">
            Dev-only · player1 · /dev/faction-rings · dark stage (BattleUI screen blend)
          </p>
        </div>
        <div className="faction-ring-screenshot-controls">
          <label>
            Faction
            <select
              value={selectedFaction}
              onChange={(event) => selectFaction(event.target.value)}
            >
              {FACTIONS.map((name) => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          </label>
          <button type="button" onClick={() => moveSelection(-1)} title="Previous faction (←)">
            Prev
          </button>
          <button type="button" onClick={() => moveSelection(1)} title="Next faction (→)">
            Next
          </button>
          <button
            type="button"
            aria-pressed={screenshotMode}
            onClick={() => setScreenshotMode((value) => !value)}
            title="Hide chrome for clean screenshots (S)"
          >
            {screenshotMode ? 'Exit screenshot mode' : 'Screenshot mode (S)'}
          </button>
        </div>
      </header>

      <main className="faction-ring-screenshot-stage">
        {/* Exact BattleUI battle-intro orb markup + screen blend. */}
        <div
          className="battle-intro-circle-orb"
          role="img"
          aria-label={selectedFaction}
        >
          <img
            src={resolvePublicAssetUrl('/images/faction_circle.png')}
            alt=""
            className="battle-intro-circle-frame"
            draggable={false}
            aria-hidden="true"
          />
          <div
            className="battle-intro-faction-fill"
            style={{ background: getFactionOrbBackground(selectedFaction) }}
          >
            <img
              src={factionIconPath}
              alt=""
              className="battle-intro-circle-icon"
              draggable={false}
              aria-hidden="true"
            />
          </div>
        </div>
      </main>
    </div>
  );
}
